import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatActivityAmount } from '../../logic/chatUtils'
import Activity from '../../model/Activity'
import ActivityLog from '../../model/ActivityLog'
import Message from '../../model/Message'
import { profilePath } from '../../navigation'
import Reaction from '../../model/Reaction'
import DateUtil from '../../util/DateUtil'
import { extractImagesFromText } from '../../util/ImageUtil'
import { cx } from '../util/cx'
import AppIcon from '../icons/AppIcon'
import Avatar, { getInitials } from '../primitives/Avatar'
import ChatImageViewer from './ChatImageViewer'
import ChatReactionDisplay from './ChatReactionDisplay'
import ChatReactionMenu from './ChatReactionMenu'
import styles from './ChatScreen.module.css'

export interface ChatMessageBubbleProps {
  message: Message
  messages: Message[]
  isOwnMessage: boolean
  isFirstInChain: boolean
  isLastInChain: boolean
  currentUserId?: string
  reactions: Reaction[]
  onReply?: (message: Message) => void
  onScrollToMessage?: (messageId: string) => void
  onEmojiReaction: (emoji: string) => Promise<void>
  onToggleReaction: (emoji: string, existingReactionId?: number) => Promise<void>
  activityLogsMap: Map<string, ActivityLog>
  activitiesMap: Map<string, Activity>
}

export default function ChatMessageBubble({
  message,
  messages,
  isOwnMessage,
  isFirstInChain,
  isLastInChain,
  currentUserId,
  reactions,
  onReply,
  onScrollToMessage,
  onEmojiReaction,
  onToggleReaction,
  activityLogsMap,
  activitiesMap,
}: ChatMessageBubbleProps) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const replyTo = message.replyToId
    ? messages.find((m) => m.id === message.replyToId)
    : undefined

  const activityData = message.activityLogId
    ? (() => {
        const activityLog = activityLogsMap.get(message.activityLogId!)
        if (!activityLog) return null
        const activity = activitiesMap.get(activityLog.activityID)
        if (!activity) return null
        return { activityLog, activity }
      })()
    : null

  const selectedEmoji = reactions.find((reaction) => reaction.userId === currentUserId)?.emoji

  function handleEmoji(emoji: string) {
    if (!currentUserId) return
    const mine = reactions.find((reaction) => reaction.userId === currentUserId)
    setMenuOpen(false)
    if (mine?.emoji === emoji && mine.id != null) {
      void onToggleReaction(emoji, mine.id)
      return
    }
    void onEmojiReaction(emoji)
  }

  function handleReply() {
    onReply?.(message)
    setMenuOpen(false)
  }

  if (activityData) {
    const { activityLog, activity } = activityData
    const displayValue = formatActivityAmount(activityLog.amount, activity.unitConversion)
    const lastNameInitial = message.user.lastName?.charAt(0).toUpperCase() ?? ''
    const displayName = `${message.user.firstName}${lastNameInitial ? ` ${lastNameInitial}.` : ''}`
    const display = Activity.getDisplayIcon(activity)
    const iconColor = activity.getIconColor()

    return (
      <ChatReactionMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        selectedEmoji={selectedEmoji}
        onEmoji={handleEmoji}
        onReply={onReply ? handleReply : undefined}
        layout="card"
      >
        <div className={styles.activityCard}>
          <div className={styles.activityTop}>
            <div className={styles.activityIcon} style={{ background: iconColor }}>
              {display.type === 'iconify' ? (
                <AppIcon icon={display.icon} size={28} />
              ) : display.src ? (
                <img src={display.src} alt="" />
              ) : null}
            </div>
            <div className={styles.activityMeta}>
              <div className={styles.activityName}>{displayName}</div>
              <div className={styles.activitySub}>logged activity</div>
            </div>
          </div>
          <div className={styles.activityBody}>
            <div className={styles.activityAmount}>
              {displayValue} {activity.unit}
            </div>
            <div className={styles.activityTitle}>{activity.title}</div>
          </div>
        </div>
        <ChatReactionDisplay reactions={reactions} side="card" />
      </ChatReactionMenu>
    )
  }

  const { images, cleanText } = extractImagesFromText(message.message)
  const continueClass = !isFirstInChain && (isOwnMessage ? styles.bubbleContinueOwn : styles.bubbleContinue)
  const nextClass = !isLastInChain && (isOwnMessage ? styles.bubbleHasNextOwn : styles.bubbleHasNext)

  return (
    <>
      <div className={cx(styles.message, isOwnMessage && styles.messageOwn)}>
        <div className={cx(styles.bubbleWrap, isOwnMessage && styles.bubbleWrapOwn)}>
        {!isOwnMessage && (
          <span className={styles.avatarSlot}>
            {isLastInChain && (
              <button
                type="button"
                className={styles.avatarButton}
                onClick={() => navigate(profilePath(message.user.id))}
              >
                <Avatar
                  initials={getInitials(message.user.firstName, message.user.lastName)}
                  photoUrl={message.user.photoUrl}
                  size="sm"
                />
              </button>
            )}
          </span>
        )}

        <div className={cx(styles.stack, isOwnMessage && styles.stackOwn)}>
          {isFirstInChain && !isOwnMessage && (
            <button
              type="button"
              className={styles.sender}
              onClick={() => navigate(profilePath(message.user.id))}
            >
              {message.user.firstName} {message.user.lastName}
            </button>
          )}

          {replyTo && (
            <button
              type="button"
              className={styles.replyPreview}
              onClick={() => onScrollToMessage?.(replyTo.id)}
            >
              <div className={styles.replyName}>{replyTo.user.firstName}</div>
              <div className={styles.replyText}>
                {replyTo.message.length > 30
                  ? `${replyTo.message.substring(0, 30)}...`
                  : replyTo.message}
              </div>
            </button>
          )}

          <ChatReactionMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            selectedEmoji={selectedEmoji}
            onEmoji={handleEmoji}
            onReply={onReply ? handleReply : undefined}
          >
            <div className={cx(styles.bubble, isOwnMessage && styles.bubbleOwn, continueClass, nextClass)}>
              {images.length > 0 && (
                <div className={styles.images}>
                  {images.map((image, index) => (
                    <button
                      key={index}
                      type="button"
                      className={styles.imageThumb}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuOpen(false)
                        setCurrentImageIndex(index)
                        setImageViewerOpen(true)
                      }}
                    >
                      <img src={image.url} alt={image.alt || `Image ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
              {cleanText && <div className={styles.bubbleText}>{cleanText}</div>}
            </div>
            <ChatReactionDisplay reactions={reactions} side={isOwnMessage ? 'own' : 'other'} />
          </ChatReactionMenu>
        </div>

        {isOwnMessage && (
          <span className={styles.avatarSlot}>
            {isLastInChain && (
              <button
                type="button"
                className={styles.avatarButton}
                onClick={() => navigate(profilePath(message.user.id))}
              >
                <Avatar
                  initials={getInitials(message.user.firstName, message.user.lastName)}
                  photoUrl={message.user.photoUrl}
                  size="sm"
                  variant="you"
                />
              </button>
            )}
          </span>
        )}
        </div>
        {isLastInChain && (
          <div className={styles.time}>{DateUtil.getRelativeTimeString(message.createdAt)}</div>
        )}
      </div>

      {imageViewerOpen && (
        <ChatImageViewer
          images={images.map((img) => img.url)}
          currentIndex={currentImageIndex}
          onClose={() => setImageViewerOpen(false)}
          onNavigate={setCurrentImageIndex}
        />
      )}
    </>
  )
}
